// ─────────────────────────────────────────
//  QPS/Orzone Import Parser
// ─────────────────────────────────────────

// Sidfoten i QPS-rapporter är TRE rader — "Orzone AB", "Gothenburg" och
// "www.orzone.com" — och upprepas på varje sida. Att bara filtrera på "orzone"
// släppte igenom "Gothenburg", som då hamnade i frågetexten. Samma mönster
// används av både text- och positionsparsern nedan.
const FOOTER_RX = /^(orzone\b|gothenburg\b|www\.)/i;

function parseQPSText(text) {
  const cleaned = text
    .split('\n')
    .filter(l => !FOOTER_RX.test(l.trim()))
    .join('\n');

  const questions = [];
  const blocks = cleaned.split(/Question\s*\d+/i); // \s* handles "Question1" if space is missing

  for (let i = 1; i < blocks.length; i++) {
    const block = blocks[i].trim();
    if (!block) continue;

    try {
      const question = parseQuestionBlock(block, i);
      if (question) questions.push(question);
    } catch (err) {
      console.error(`Error parsing question ${i}:`, err);
    }
  }

  return questions;
}

function parseQuestionBlock(block, questionNum) {
  const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
  let questionText = '';
  // Each entry: { text: string, isCorrect: boolean }
  const optionsList = [];
  let foundFirstOption = false;

  for (const line of lines) {
    // Bug 3 fix: QPS PDFs use Font Awesome Private Use Area characters, not standard Unicode.
    //   \uf10c (U+F10C, fa-circle-o) = wrong option
    //   \uf00c (U+F00C, fa-check)    = correct option
    const isOption = /^[\uf10c\uf00c]/.test(line);

    if (isOption) {
      foundFirstOption = true;
      const isCorrect = line.startsWith('\uf00c'); // Bug 3 fix
      const optionText = line.replace(/^[\uf10c\uf00c]\s*/, '').trim();
      if (optionText) {
        optionsList.push({ text: optionText, isCorrect });
      }
    } else if (foundFirstOption && optionsList.length > 0) {
      // Bug 4 fix: long options wrap to the next line without a prefix marker.
      // Append continuation text to the most recent option instead of discarding.
      optionsList[optionsList.length - 1].text += ' ' + line;
    } else {
      // Still in the question-text section (before any options)
      if (questionText) questionText += ' ' + line;
      else questionText = line;
    }
  }

  if (!questionText || optionsList.length === 0) {
    return null;
  }

  const correctIndex = optionsList.findIndex(o => o.isCorrect);
  if (correctIndex === -1) {
    // No correct answer marked — skip this block
    return null;
  }

  return {
    text: questionText,
    rationale: '',
    options: optionsList.reduce((acc, opt, idx) => {
      acc[String.fromCharCode(65 + idx)] = opt.text;
      return acc;
    }, {}),
    correctAnswer: correctIndex
  };
}

async function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const XLSX = await import("xlsx");
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const questions = parseExcelWorkbook(workbook, XLSX);
        resolve(questions);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Rubrikraden styr kolumnerna när den känns igen: "Fråga | A | B | C | D |
// Rätt svar | Förklaring" (mallen), i valfri ordning efter frågan. Utan
// igenkänd svarskolumn gäller det gamla formatet: frågan först, alternativen
// sedan och rätt svar i sista kolumnen.
const EXCEL_ANSWER_RX = /^(?:rätt(?:a)?(?: svar)?|korrekt svar|svar|facit|correct(?: answer)?|answer|key)$/i;
const EXCEL_RATIONALE_RX = /^(?:förklaring|motivering|återkoppling|kommentar|explanation|rationale|feedback)$/i;

function parseExcelWorkbook(workbook, XLSX) {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  const header = (data[0] || []).map(h => String(h ?? '').trim());
  const answerCol = header.findIndex((h, i) => i > 0 && EXCEL_ANSWER_RX.test(h));
  const rationaleCol = header.findIndex((h, i) => i > 0 && EXCEL_RATIONALE_RX.test(h));
  const useHeader = answerCol > 0;

  const questions = [];
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    if (!row || row.length === 0) continue;

    const questionText = row[0];
    if (!questionText) continue;

    // Alternativkolumnerna, och vilken bokstav i svarskolumnen som pekar på
    // vilken. Bokstäverna delas ut i tur och ordning över ICKE-tomma celler, så
    // en tom kolumn C inte får svaret att peka på fel alternativ.
    let optionCols;
    let answerCell;
    if (useHeader) {
      const lettered = header.map((h, j) => (j > 0 && /^[A-F]$/i.test(h) ? j : -1)).filter(j => j > 0);
      optionCols = lettered.length
        ? lettered
        : header.map((_, j) => j).filter(j => j > 0 && j !== answerCol && j !== rationaleCol);
      answerCell = row[answerCol];
    } else {
      optionCols = [];
      for (let j = 1; j <= 6 && j < row.length - 1; j++) optionCols.push(j);
      answerCell = row[row.length - 1];
    }

    const options = {};
    const indexByLetter = {}; // bokstav i källan → index bland de icke-tomma
    let numOptions = 0;
    optionCols.forEach((j, pos) => {
      const value = row[j];
      if (value === undefined || value === null || String(value).trim() === '') return;
      options[String.fromCharCode(65 + numOptions)] = String(value).trim();
      indexByLetter[String.fromCharCode(65 + pos)] = numOptions;
      numOptions++;
    });

    let correctIndex = null;
    if (answerCell !== undefined && answerCell !== null) {
      const letter = String(answerCell).trim().toUpperCase().charAt(0);
      if (/[A-F]/.test(letter)) correctIndex = indexByLetter[letter] ?? null;
    }

    const rationale = useHeader && rationaleCol > 0 && row[rationaleCol] ? String(row[rationaleCol]).trim() : '';

    if (numOptions >= 2 && (useHeader || correctIndex !== null)) {
      questions.push({
        text: String(questionText).trim(),
        rationale,
        options,
        correctAnswer: correctIndex,
      });
    }
  }

  return questions;
}

// Mallen som visas och laddas ner i importvyn
const EXCEL_TEMPLATE = {
  sv: [
    ["Fråga", "A", "B", "C", "D", "Rätt svar", "Förklaring"],
    ["Vilken vektor sprider TBE i Sverige?", "Mygga", "Fästing", "Sandmygga", "Knott", "B", "TBE sprids med fästingar."],
    ["Vilket läkemedel är förstahandsval vid tularemi?", "Klindamycin", "Cefadroxil", "Ciprofloxacin", "Amoxicillin", "C", ""],
  ],
  en: [
    ["Question", "A", "B", "C", "D", "Answer", "Explanation"],
    ["Which vector spreads TBE in Sweden?", "Mosquito", "Tick", "Sandfly", "Blackfly", "B", "TBE is spread by ticks."],
    ["Which drug is first-line for tularemia?", "Clindamycin", "Cefadroxil", "Ciprofloxacin", "Amoxicillin", "C", ""],
  ],
};

async function downloadExcelTemplate(lang) {
  const XLSX = await import("xlsx");
  const ws = XLSX.utils.aoa_to_sheet(EXCEL_TEMPLATE[lang] || EXCEL_TEMPLATE.sv);
  ws["!cols"] = [{ wch: 50 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 40 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, lang === "en" ? "Questions" : "Frågor");
  XLSX.writeFile(wb, lang === "en" ? "tRAT-question-template.xlsx" : "tRAT-fragemall.xlsx");
}

// PUA-glyferna QPS använder som svarsmarkörer
const ICON_CHECK = 0xf00c;  // fa-check      → rätt alternativ
const ICON_CIRCLE = 0xf10c; // fa-circle-o   → fel alternativ

async function loadPdfJs() {
  // pdf.js 4.x är ESM-only. Workern ligger som statisk fil i public/
  // (se scripts/copy-pdf-worker.js) — 4.x har ingen bundlingsbar worker-entry.
  const pdfjsLib = await import("pdfjs-dist/build/pdf.mjs");
  const base = process.env.PUBLIC_URL || "";
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${base}/pdf.worker.min.mjs`;
  return pdfjsLib;
}

// Skarvar textobjekten på en rad. Mellanslag bara där det finns ett: i
// strängarna själva, som ett eget blankteckenobjekt eller som ett synligt
// glapp. Ett fetstilt ord mitt i en mening är ett eget textobjekt som börjar
// direkt efter parentesen, och med alltid-mellanslag blev det
// "CFS ( Clinical Frailty Scale)". Saknas bredden (äldre pdf.js) blir det
// mellanslag som förut.
function joinLineItems(items) {
  let out = "";
  let prev = null;
  let pendingSpace = false;
  for (const it of items) {
    if (it.space) { pendingSpace = true; continue; }
    if (prev) {
      const hasGeometry = typeof prev.w === "number" && typeof it.xf === "number" && typeof prev.xf === "number";
      const gap = hasGeometry ? it.xf - (prev.xf + prev.w) : Infinity;
      const minGap = Math.max(1, 0.15 * Math.max(prev.h || 0, it.h || 0));
      if (pendingSpace || prev.trailingSpace || it.leadingSpace || gap > minGap) out += " ";
    }
    out += it.str;
    prev = it;
    pendingSpace = false;
  }
  return out;
}

// Stycken i frågetexten. QPS lägger större radavstånd mellan stycken
// ("Begrepp: …", fallbeskrivningen, själva frågan) än mellan raderna inom ett
// stycke: i Orzone-exporten ~17–18 pt mot ~12 pt vid 9,5 pt text. Ibland står
// den fetstilta frågan direkt under fallet utan extra avstånd, och då är
// typsnittsbytet enda tecknet. Över en sidbrytning säger varken avstånd eller
// typsnitt något säkert, så där fortsätter stycket.
function isParagraphBreak(prev, cur) {
  if (prev.page !== cur.page) return false;
  const lineH = Math.max(prev.h || 0, cur.h || 0);
  if (lineH > 0 && cur.absY - prev.absY > 1.5 * lineH) return true;
  const shared = [...cur.fonts].some(f => prev.fonts.has(f));
  return prev.fonts.size > 0 && cur.fonts.size > 0 && !shared;
}

// Raderna blir en text med "\n" mellan styckena och mellanslag inom dem.
function joinParagraphs(lines) {
  let out = "";
  lines.forEach((l, i) => {
    if (i > 0) out += isParagraphBreak(lines[i - 1], l) ? "\n" : " ";
    out += l.text;
  });
  return out.replace(/[ \t]+/g, " ").replace(/ *\n */g, "\n").trim();
}

// ─────────────────────────────────────────
//  Positionsbaserad QPS-parser
// ─────────────────────────────────────────
// Läser råa positioner från pdf.js i stället för att platta ut allt till text.
// Det löser tre saker som textvarianten inte klarade:
//   • sidfoten filtreras bort per textobjekt, inte per rad i en hopslagen sträng
//   • frågehuvuden som QPS upprepar vid sidbrytning avdupliceras
//   • svarsalternativ grupperas efter sina markörer, så alternativ som radbryts
//     hamnar hos rätt alternativ
// Frågor står vid x ≈ 44, alternativens text vid x ≈ 70.
async function parseQpsPdfPositional(file, pdfjsLib) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  // Steg 1: samla alla textobjekt med position, y som absolut över sidor
  const allItems = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const tc = await page.getTextContent();
    const pageHeight = (page.view[3] - page.view[1]) || 842;
    for (const item of tc.items) {
      if (item.str === undefined || item.str === null) continue;
      const absY = (p - 1) * 10000 + (pageHeight - item.transform[5]);
      allItems.push({
        str: item.str,
        x: Math.round(item.transform[4]),
        xf: item.transform[4],
        w: item.width,
        h: item.height,
        font: item.fontName,
        absY,
        codes: [...item.str].map(c => c.codePointAt(0)),
        page: p,
      });
    }
  }
  allItems.sort((a, b) => a.absY - b.absY);

  // Steg 2: hitta markörer och frågehuvuden
  const markers = [];
  const rawHeaders = [];
  for (const item of allItems) {
    if (FOOTER_RX.test(item.str.trim())) continue;
    const qMatch = item.str.match(/^Question\s+(\d+)/i);
    if (qMatch) {
      const idMatch = item.str.match(/\(#\s*(\d+)\s*\)/);
      rawHeaders.push({ absY: item.absY, num: parseInt(qMatch[1]), qid: idMatch ? idMatch[1] : null });
      continue;
    }
    for (const code of item.codes) {
      if (code === ICON_CHECK || code === ICON_CIRCLE) {
        markers.push({ absY: item.absY, isCorrect: code === ICON_CHECK });
      }
    }
  }

  // QPS upprepar frågehuvudet vid sidbrytning (nederst + överst på nästa sida).
  // Behåll det sista av två intilliggande med samma nummer.
  const questionHeaders = [];
  for (let i = 0; i < rawHeaders.length; i++) {
    if (rawHeaders[i + 1] && rawHeaders[i + 1].num === rawHeaders[i].num) continue;
    questionHeaders.push(rawHeaders[i]);
  }

  // Steg 3: gruppera textobjekt till rader
  const LINE_TOL = 5;
  const lineGroups = [];
  for (const item of allItems) {
    if (!item.str) continue;
    if (item.codes.length === 1 && (item.codes[0] === ICON_CHECK || item.codes[0] === ICON_CIRCLE)) continue;
    if (FOOTER_RX.test(item.str.trim())) continue;
    let grp = lineGroups.find(g => Math.abs(g.absY - item.absY) <= LINE_TOL);
    if (!grp) { grp = { absY: item.absY, page: item.page, items: [] }; lineGroups.push(grp); }
    const raw = item.str.replace(/[\uf00c\uf10c\uf105]/g, "");
    const clean = raw.trim();
    // Ett textobjekt som bara är blanktecken är ett mellanslag mellan två ord.
    // Det behålls som markör så att orden inte klistras ihop när de skarvas.
    grp.items.push(clean
      ? { x: item.x, xf: item.xf, w: item.w, h: item.h, font: item.font, str: clean,
          leadingSpace: /^\s/.test(raw), trailingSpace: /\s$/.test(raw) }
      : { x: item.x, xf: item.xf, space: true });
  }
  lineGroups.sort((a, b) => a.absY - b.absY);

  const lines = lineGroups.map(grp => {
    const sorted = grp.items.sort((a, b) => a.x - b.x);
    const words = sorted.filter(s => !s.space);
    return {
      absY: grp.absY,
      page: grp.page,
      minX: words[0]?.x || 0,
      h: Math.max(0, ...words.map(s => s.h || 0)),
      fonts: new Set(words.map(s => s.font).filter(Boolean)),
      // C0-kontrolltecken bort (matchas inte av \s) — de följer ibland med ur
      // PDF:en och gör strängen ogiltig som jsonb
      text: joinLineItems(sorted)
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "").replace(/\s+/g, " ").trim(),
    };
  }).filter(l => l.text.length > 0);

  // Steg 4: bygg frågorna
  const questions = [];
  const numberByIndex = [];
  for (let qi = 0; qi < questionHeaders.length; qi++) {
    const qh = questionHeaders[qi];
    const nextQh = questionHeaders[qi + 1];
    const inRange = y => y > qh.absY && (!nextQh || y < nextQh.absY);

    const qMarkers = markers.filter(m => inRange(m.absY));
    if (qMarkers.length === 0) continue;

    const contentLines = lines
      .filter(l => inRange(l.absY))
      .filter(l => !/^Question\s+\d+/i.test(l.text));

    const firstMarkerY = qMarkers[0].absY;
    const optionAreaStart = firstMarkerY - 8;
    const questionText = joinParagraphs(contentLines
      .filter(l => l.absY < optionAreaStart || (l.minX < 50 && l.absY < firstMarkerY)));

    // Markörerna avgränsar alternativen; mittpunkten mellan två markörer är
    // gränsen, så text som börjar strax före sin egen markör följer med rätt.
    const options = [];
    for (let mi = 0; mi < qMarkers.length; mi++) {
      const markerY = qMarkers[mi].absY;
      const prevY = qMarkers[mi - 1]?.absY;
      const nextY = qMarkers[mi + 1]?.absY;
      const lower = prevY ? (prevY + markerY) / 2 : optionAreaStart;
      const upper = nextY ? (markerY + nextY) / 2 : Infinity;
      const text = contentLines
        .filter(l => l.absY >= lower && l.absY < upper && l.minX >= 50)
        .map(l => l.text).join(" ").replace(/\s+/g, " ").trim();
      options.push({ text, isCorrect: qMarkers[mi].isCorrect });
    }

    // Alternativ vars text ligger i en bild (t.ex. "A/B/C/D" med figur under)
    // har tom text — behåll dem ändå, annars tappas hela frågan.
    const hasAnyText = options.some(o => o.text.length > 0);
    const kept = hasAnyText ? options.filter(o => o.text.length > 0) : options;
    if (kept.length < 2) continue;

    const correctIndex = kept.findIndex(o => o.isCorrect);
    if (correctIndex === -1) continue;

    const optionsObj = {};
    kept.forEach((o, i) => { optionsObj[String.fromCharCode(65 + i)] = o.text; });

    questions.push({
      text: questionText,
      rationale: "",
      options: optionsObj,
      correctAnswer: correctIndex,
      questionId: qh.qid || null,
    });
    numberByIndex.push(qh.num);
  }

  return { pdf, questions, questionHeaders, numberByIndex };
}

// ─────────────────────────────────────────
//  Bildextraktion ur QPS-PDF
// ─────────────────────────────────────────
// En del frågor har hela sitt innehåll i en figur — svarsalternativen kan vara
// bara "A/B/C/D" med bilden under. Utan bilderna är de frågorna obesvarbara.
//
// pdf.js ger oss bildernas placering via operatorlistan. Vi renderar sidan en
// gång och beskär varje bild ur den, i stället för att avkoda bildströmmarna
// själva (som kräver hantering av varje filter/färgrymd).
//
// Loggor, sidramar och header-/footer-band filtreras bort på storlek och läge.
//
// QPS ritar dessutom ett kortliknande bakgrundsobjekt bakom varje frågeblock.
// Det är en bild i PDF:en, lika bred som textspalten, och beskärs det ut ser
// resultatet ut som en skärmdump av hela frågan (huvud, alternativ och facit).
// På sidor med flera frågor spänner det nästan hela sidan och fångas av
// ramfiltret, men på en sida med bara en fråga är det kort nog att slinka
// igenom — och eftersom det börjar strax OVANFÖR frågehuvudet knyts det till
// föregående fråga. Det var så fråga 20 hamnade som bild i fråga 19.
//
// Bakgrunden känns igen på att sidans text ligger ovanpå den: en riktig figur
// har ingen PDF-text inuti sin ruta.
const BACKGROUND_TEXT_ITEMS = 3;

function isBackgroundImage(rect, textBoxes) {
  let inside = 0;
  for (const t of textBoxes) {
    if (t.x >= rect.minX && t.x <= rect.minX + rect.w
        && t.yTop >= rect.yTop && t.yTop <= rect.yTop + rect.h) {
      inside++;
      if (inside >= BACKGROUND_TEXT_ITEMS) return true;
    }
  }
  return false;
}

async function extractQpsImages(pdf, pdfjsLib, questionHeaders, numberByIndex) {
  const OPS = pdfjsLib.OPS;
  const SCALE = 2;
  const byQuestionNumber = {};
  const mul = (m, n) => [
    m[0]*n[0]+m[2]*n[1], m[1]*n[0]+m[3]*n[1],
    m[0]*n[2]+m[2]*n[3], m[1]*n[2]+m[3]*n[3],
    m[0]*n[4]+m[2]*n[5]+m[4], m[1]*n[4]+m[3]*n[5]+m[5],
  ];

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const pageWidth = (page.view[2] - page.view[0]) || 595;
    const pageHeight = (page.view[3] - page.view[1]) || 842;
    let opList;
    try { opList = await page.getOperatorList(); } catch { continue; }

    // Textens position på sidan, för att skilja figurer från frågekortets
    // bakgrund. Sidfoten räknas inte — den ligger utanför alla figurer ändå.
    let textBoxes = [];
    try {
      const tc = await page.getTextContent();
      textBoxes = tc.items
        .filter(it => it.str && it.str.trim() && !FOOTER_RX.test(it.str.trim()))
        .map(it => ({ x: it.transform[4], yTop: pageHeight - it.transform[5] }));
    } catch { /* utan text kan vi inte utesluta bakgrunder — ta bilden ändå */ }

    let ctm = [1, 0, 0, 1, 0, 0];
    const stack = [];
    const found = [];
    for (let i = 0; i < opList.fnArray.length; i++) {
      const fn = opList.fnArray[i];
      const args = opList.argsArray[i];
      if (fn === OPS.save) stack.push(ctm.slice());
      else if (fn === OPS.restore) ctm = stack.pop() || [1, 0, 0, 1, 0, 0];
      else if (fn === OPS.transform) ctm = mul(ctm, args);
      else if (fn === OPS.paintImageXObject || fn === OPS.paintJpegXObject
               || fn === OPS.paintImageMaskXObject || fn === OPS.paintInlineImageXObject) {
        const [a, b, c, d, e, f] = ctm;
        const xs = [e, a + e, c + e, a + c + e];
        const ys = [f, b + f, d + f, b + d + f];
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const w = maxX - minX, h = maxY - minY;
        const yTop = pageHeight - maxY;
        const wfrac = w / pageWidth, hfrac = h / pageHeight;
        const topFrac = yTop / pageHeight, botFrac = (yTop + h) / pageHeight;
        // Sidram/bakgrund spänner hela sidan; ett innehållsfoto slutar ovanför
        // sidfoten även när det fyller större delen av sidan.
        if (topFrac < 0.12 && botFrac > 0.88) continue; // dekorativ ram
        if (hfrac < 0.08 || wfrac < 0.15) continue;     // logga/ikon
        if (botFrac < 0.12) continue;                   // header-band
        if (topFrac > 0.90) continue;                   // footer-band
        if (isBackgroundImage({ minX, yTop, w, h }, textBoxes)) continue; // frågekortets bakgrund
        found.push({ minX, yTop, w, h, absYTop: (p - 1) * 10000 + yTop });
      }
    }
    if (found.length === 0) continue;

    const viewport = page.getViewport({ scale: SCALE });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;

    for (const im of found) {
      // Knyt bilden till frågan vars huvud ligger närmast ovanför den
      let qNum = null;
      for (const qh of questionHeaders) {
        if (qh.absY <= im.absYTop) qNum = qh.num; else break;
      }
      if (qNum == null) continue;
      const crop = document.createElement("canvas");
      crop.width = Math.max(1, Math.round(im.w * SCALE));
      crop.height = Math.max(1, Math.round(im.h * SCALE));
      crop.getContext("2d").drawImage(
        canvas, im.minX * SCALE, im.yTop * SCALE, im.w * SCALE, im.h * SCALE,
        0, 0, crop.width, crop.height
      );
      const blob = await new Promise(res => crop.toBlob(res, "image/jpeg", 0.85));
      if (!blob) continue;
      const key = String(qNum);
      if (!byQuestionNumber[key]) byQuestionNumber[key] = [];
      byQuestionNumber[key].push({
        blob,
        previewUrl: URL.createObjectURL(blob),
        w: Math.round(im.w), h: Math.round(im.h),
      });
    }
  }

  // Mappa om från QPS-frågenummer till index i questions-arrayen
  const byIndex = {};
  numberByIndex.forEach((num, idx) => {
    const imgs = byQuestionNumber[String(num)];
    if (imgs && imgs.length) byIndex[idx] = imgs;
  });
  return byIndex;
}

// Parsar en QPS-PDF. Returnerar samma form som de andra parsrarna (en array av
// frågor); frågor med figurer får dessutom q.images = [{ blob, previewUrl, … }],
// som laddas upp till Storage när tRAT:en sparas.
async function parsePDFFile(file) {
  const pdfjsLib = await loadPdfJs();
  const { pdf, questions, questionHeaders, numberByIndex } =
    await parseQpsPdfPositional(file, pdfjsLib);
  // Inga QPS-frågor: ett eget dokument ("Fråga 1 … A. … Facit"). Figurer
  // plockas bara ur QPS-rapporter, där de går att knyta till rätt fråga.
  if (questions.length === 0) return parsePlainText(await pdfToText(pdf));
  try {
    const byIndex = await extractQpsImages(pdf, pdfjsLib, questionHeaders, numberByIndex);
    for (const [idx, imgs] of Object.entries(byIndex)) {
      if (questions[idx]) questions[idx].images = imgs;
    }
  } catch (err) {
    // Bildextraktion är ett tillägg — en fråga utan figur är bättre än ett
    // helt misslyckat importflöde
    console.error("extractQpsImages:", err);
  }
  return questions;
}

// ─────────────────────────────────────────
//  Generisk textparser (egna frågedokument)
// ─────────────────────────────────────────
// Lärare som skriver frågor själva i Word har inga QPS-glyfer. Ett typiskt
// dokument ser ut så här:
//
//   Fråga 1. Infektionssjukdomar        ← rubrik, ev. med ämne efter numret
//   En patient … Vilket av följande …?  ← frågetext
//   A. …  B. …  C. …  D. …              ← alternativ, radbrytningar tillåtna
//   Förklaring: …                       ← blir återkoppling (rationale)
//   …
//   Facit                               ← rätta svar samlade sist
//   1. B (Infektionssjukdomar)
//
// Rätt svar kan också stå i frågan ("Rätt svar: B") eller markeras vid
// alternativet ("B. … *", "(rätt)", "✓"). Saknas det blir correctAnswer null,
// och läraren väljer det i förhandsvisningen.

const KEYWORD_HEADER_RX = /^(?:fråga|question|uppgift)\s*(\d{1,3})\s*[.:)\-–—]?\s*(.*)$/i;
const NUMERIC_HEADER_RX = /^(\d{1,3})\s*[.)]\s+(.+)$/;
const OPTION_RX = /^\(?([A-Fa-f])\s*[.):]\s*(.*)$/;
const RATIONALE_RX = /^(?:förklaring|motivering|kommentar|återkoppling|explanation|rationale|feedback)\s*:\s*(.*)$/i;
const INLINE_ANSWER_RX = /^(?:rätt(?:a)? svar|korrekt svar|svar|correct answer|answer)\s*[:\-–]\s*\(?([A-Fa-f])\b/i;
const KEY_HEADER_RX = /^(?:facit|svarsnyckel|rätta svar|answer key|answers|svar)\s*:?\s*(.*)$/i;
// "1. B", "1B", "1-B", "1: b", flera per rad. Bokstaven får inte följas av en
// bokstav, så "1. Borrelia" i en facitrad räknas inte som svar B.
const KEY_ENTRY_RX = /(?:^|[\s,;])(\d{1,3})\s*[.):\-–]?\s*([A-Fa-f])(?![A-Za-zÅÄÖåäöÉé])/g;
const CORRECT_MARK_RX = /\s*(?:\*+|\((?:rätt|korrekt|correct)\)|\[x\]|[✓✔])\s*$/i;
const CORRECT_MARK_START_RX = /^(?:\*+|[✓✔])\s*/;
const PAGE_NUMBER_RX = /^(?:(?:sida|page)\s+)?\d{1,3}(?:\s*(?:av|of|\/)\s*\d{1,3})?$/i;

// Rader som slutar med bindestreck fogas ihop utan mellanslag
function appendLine(base, line) {
  if (!base) return line;
  return base.endsWith('-') ? base + line : base + ' ' + line;
}

function letterIndex(letter) {
  return letter.toUpperCase().charCodeAt(0) - 65;
}

function parseAnswerKey(lines) {
  const key = {};
  for (const line of lines) {
    for (const m of line.matchAll(KEY_ENTRY_RX)) {
      const num = parseInt(m[1], 10);
      if (!(num in key)) key[num] = letterIndex(m[2]);
    }
  }
  return key;
}

function parsePlainText(text) {
  const lines = text
    .split(/\r?\n/)
    .map(l => l.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '').replace(/\s+/g, ' ').trim())
    .filter(l => l && !PAGE_NUMBER_RX.test(l));

  // Med "Fråga N"-rubriker räknas bara de; annars "N." med löpande numrering,
  // så att en numrerad rad i en frågetext inte startar en ny fråga.
  const useKeyword = lines.some(l => KEYWORD_HEADER_RX.test(l));

  const blocks = [];
  const keyLines = [];
  let current = null;
  let inKey = false;

  for (const line of lines) {
    let header = null;
    if (useKeyword) {
      const m = line.match(KEYWORD_HEADER_RX);
      if (m) header = { num: parseInt(m[1], 10), rest: m[2] };
    } else if (!inKey) {
      const m = line.match(NUMERIC_HEADER_RX);
      const expected = blocks.length ? blocks[blocks.length - 1].num + 1 : null;
      if (m && (expected === null || parseInt(m[1], 10) === expected)) {
        header = { num: parseInt(m[1], 10), rest: m[2] };
      }
    }
    if (header) {
      inKey = false;
      current = { num: header.num, rest: header.rest, lines: [] };
      blocks.push(current);
      continue;
    }

    const keyMatch = blocks.length && line.match(KEY_HEADER_RX);
    if (keyMatch && (keyMatch[1] === '' || KEY_ENTRY_RX.test(keyMatch[1]))) {
      KEY_ENTRY_RX.lastIndex = 0;
      inKey = true;
      current = null;
      if (keyMatch[1]) keyLines.push(keyMatch[1]);
      continue;
    }
    if (inKey) keyLines.push(line);
    else if (current) current.lines.push(line);
  }

  const answerKey = parseAnswerKey(keyLines);
  const questions = [];

  for (const block of blocks) {
    let stem = '';
    let rationale = '';
    const options = [];
    let correct = null;
    let section = 'stem';

    for (const line of block.lines) {
      const ans = line.match(INLINE_ANSWER_RX);
      if (ans && options.length) { correct = letterIndex(ans[1]); section = 'after'; continue; }

      const rat = line.match(RATIONALE_RX);
      if (rat) { section = 'rationale'; rationale = rat[1]; continue; }

      const opt = section !== 'rationale' && line.match(OPTION_RX);
      if (opt && letterIndex(opt[1]) === options.length) {
        section = 'options';
        let optText = opt[2];
        if (CORRECT_MARK_RX.test(optText) || CORRECT_MARK_START_RX.test(optText)) {
          correct = options.length;
          optText = optText.replace(CORRECT_MARK_RX, '').replace(CORRECT_MARK_START_RX, '');
        }
        options.push(optText);
        continue;
      }

      if (section === 'stem') stem = appendLine(stem, line);
      else if (section === 'options') {
        let cont = line;
        if (CORRECT_MARK_RX.test(cont)) {
          correct = options.length - 1;
          cont = cont.replace(CORRECT_MARK_RX, '');
        }
        options[options.length - 1] = appendLine(options[options.length - 1], cont);
      } else if (section === 'rationale') rationale = appendLine(rationale, line);
    }

    // Text efter numret är en ämnesrubrik ("Fråga 9. Tularemi – diagnos") om
    // den är kort och frågetexten följer på egna rader; annars börjar själva
    // frågan redan på rubrikraden.
    const isTitle = stem && block.rest.length <= 60 && !/[?.:]$/.test(block.rest);
    if (block.rest && !isTitle) stem = appendLine(block.rest, stem);
    if (!stem || options.length < 2) continue;

    if (correct === null && block.num in answerKey) correct = answerKey[block.num];
    if (correct !== null && correct >= options.length) correct = null;

    questions.push({
      text: stem.trim(),
      rationale: rationale.trim(),
      options: options.reduce((acc, o, i) => {
        acc[String.fromCharCode(65 + i)] = o.trim();
        return acc;
      }, {}),
      correctAnswer: correct,
    });
  }

  return questions;
}

// Klistrad text: QPS-glyfer → QPS-parsern, annars den generiska
function parseQuestionText(text) {
  return /[]/.test(text) ? parseQPSText(text) : parsePlainText(text);
}

// Bygger rader ur en PDF med pdf.js: textobjekt på samma höjd blir en rad,
// och mellanslag läggs in där det finns ett glapp mellan objekten.
async function pdfToText(pdf) {
  const out = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const tc = await page.getTextContent();
    const rows = [];
    for (const item of tc.items) {
      if (!item.str) continue;
      const y = item.transform[5];
      let row = rows.find(r => Math.abs(r.y - y) <= 3);
      if (!row) { row = { y, items: [] }; rows.push(row); }
      row.items.push({ x: item.transform[4], w: item.width || 0, str: item.str });
    }
    rows.sort((a, b) => b.y - a.y);
    for (const row of rows) {
      row.items.sort((a, b) => a.x - b.x);
      let line = '';
      let prevEnd = null;
      for (const it of row.items) {
        if (prevEnd !== null && it.x - prevEnd > 1 && !/\s$/.test(line) && !/^\s/.test(it.str)) line += ' ';
        line += it.str;
        prevEnd = it.x + it.w;
      }
      out.push(line);
    }
  }
  return out.join('\n');
}

// ─────────────────────────────────────────
//  Word (.docx)
// ─────────────────────────────────────────
// mammoth gör om dokumentet till HTML. Automatiska listor i Word tappar sin
// numrering på vägen ("A." och "1." är formatering, inte text), så den sätts
// tillbaka här innan den generiska textparsern tar vid:
//   • "Fråga N"-rubriker i dokumentet → numrerade listor är alternativ (A, B, …)
//   • nästlade listor → yttre nivån är frågor (1, 2, …), inre alternativ
//   • platta listor och alternativ skrivna för hand ("A. …") → listan är frågor
//   • annars är listorna alternativ
// Listor efter en facitrubrik numreras alltid, så "1. B" överlever.
function docxHtmlToText(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const items = [];
  let listId = 0;
  const isList = el => el.tagName === "OL" || el.tagName === "UL";

  function walkList(list, depth) {
    const id = ++listId;
    for (const li of list.children) {
      if (li.tagName !== "LI") continue;
      const own = [...li.childNodes]
        .filter(n => !(n.nodeType === 1 && isList(n)))
        .map(n => n.textContent).join("").trim();
      if (own) items.push({ text: own, depth, list: id, ordered: list.tagName === "OL" });
      for (const sub of li.children) if (isList(sub)) walkList(sub, depth + 1);
    }
  }
  for (const el of doc.body.children) {
    if (isList(el)) walkList(el, 0);
    else if (el.tagName === "TABLE") {
      for (const tr of el.querySelectorAll("tr")) {
        const cells = [...tr.children].map(td => td.textContent.trim()).filter(Boolean);
        if (cells.length) items.push({ text: cells.join(" "), depth: null });
      }
    } else {
      const text = el.textContent.trim();
      if (text) items.push({ text, depth: null });
    }
  }

  const plain = items.filter(it => it.depth === null || !it.ordered).map(it => it.text);
  const hasKeyword = plain.some(l => KEYWORD_HEADER_RX.test(l));
  const hasNested = items.some(it => it.ordered && it.depth > 0);
  const hasPlainOptions = plain.some(l => OPTION_RX.test(l));
  const listsAreQuestions = !hasKeyword && (hasNested || hasPlainOptions);

  const lines = [];
  let questionNo = 0;
  let keyNo = 0;
  let inKey = false;
  let lastList = null;
  let letter = 0;
  for (const it of items) {
    if (it.depth === null || !it.ordered) {
      // Bara en ren rubrik ("Facit"), inte "Svar: B" under en fråga
      const key = lines.length && !KEYWORD_HEADER_RX.test(it.text) && it.text.match(KEY_HEADER_RX);
      if (key && key[1] === '') inKey = true;
      lines.push(it.text);
      continue;
    }
    if (it.list !== lastList) { letter = 0; lastList = it.list; }
    if (inKey) lines.push(`${++keyNo}. ${it.text}`);
    else if (listsAreQuestions && it.depth === 0) lines.push(`${++questionNo}. ${it.text}`);
    else lines.push(`${String.fromCharCode(65 + letter++)}. ${it.text}`);
  }
  return lines.join("\n");
}

async function parseDocxFile(file) {
  const mammoth = await import("mammoth");
  const arrayBuffer = await file.arrayBuffer();
  // Webbläsarbygget av mammoth läser arrayBuffer, Node-bygget (tester) buffer
  const { value } = await mammoth.convertToHtml({ arrayBuffer, buffer: arrayBuffer });
  return parsePlainText(docxHtmlToText(value));
}

function validateQuestions(questions, { allowMissingAnswer = false } = {}) {
  const errors = [];
  
  if (questions.length === 0) {
    errors.push('No questions found');
    return { valid: false, errors };
  }
  
  questions.forEach((q, i) => {
    if (!q.text) errors.push(`Question ${i + 1}: Missing question text`);
    
    const optionCount = Object.keys(q.options).length;
    if (optionCount < 2) errors.push(`Question ${i + 1}: Must have at least 2 options`);
    if ((q.correctAnswer === null || q.correctAnswer === undefined) && !allowMissingAnswer) {
      errors.push(`Question ${i + 1}: Missing correct answer`);
    }
    if (q.correctAnswer >= optionCount) {
      errors.push(`Question ${i + 1}: Correct answer index out of range`);
    }
  });
  
  return { valid: errors.length === 0, errors };
}

export { parseDocxFile, docxHtmlToText, downloadExcelTemplate, EXCEL_TEMPLATE, parsePlainText, parseQuestionText, pdfToText, parseQPSText, parseQuestionBlock, parseExcelFile, parseExcelWorkbook, parsePDFFile, parseQpsPdfPositional, isBackgroundImage, FOOTER_RX, validateQuestions };
